import { useEffect, useMemo, useRef, useState } from 'react'
import type { DeviceSpecDevice, SpecMachine, SpecMachineItem } from '../../types'
import { addressKey } from './pullMerge'
import { deviceLabel } from './masterView'
import { newKey } from './model'

// 태그 불러오기 — 왼쪽 디바이스, 오른쪽 그 디바이스의 태그(체크·머리줄 전체 선택·검색). [추가] 로 고른 태그를 지금 인터페이스 항목 끝에 한 번에 넣는다.
// 전송코드 = {인터페이스 ID}_{태그 Alias} (Alias 가 없으면 비움), 설명 = 태그 설명. 아래 [ID_ 붙이기] 를 끄면 Alias 만 넣는다.
// 선택은 디바이스와 무관하게 한 집합 — 다른 디바이스로 옮겨 가도 앞에서 고른 태그가 유지된다. 이미 넣은 태그도 표시만 하고 넣을 수 있다.

const pick = (device: string, address: string) => `${device}\u0000${address}`

/** 넣을 전송코드 — prefix 면 {인터페이스 ID}_{Alias} (ID 나 Alias 가 비면 ''), 아니면 Alias 만 */
const codeOf = (machineCode: string, alias: string, prefix: boolean) => {
  const id = machineCode.trim(), a = alias.trim()
  return !a ? '' : !prefix ? a : id ? `${id}_${a}` : ''
}

export default function AddTagsDialog({ machine, devices, onAdd, onClose }: {
  machine: SpecMachine
  devices: DeviceSpecDevice[]
  onAdd: (items: SpecMachineItem[]) => void
  onClose: () => void
}) {
  const withTags = devices.filter(d => d.tags.some(t => t.address.trim()))
  const [deviceKey, setDeviceKey] = useState(withTags[0]?.key ?? '')
  const [selected, setSelected] = useState<string[]>([]) // 고른 순서 유지 (pick 키)
  const [query, setQuery] = useState('')
  // 전송코드 앞에 {인터페이스 ID}_ 를 붙일지 — 기본은 붙인다 (규칙). 끄면 Alias 그대로
  const [prefix, setPrefix] = useState(true)

  // 이미 이 인터페이스에 있는 태그 (디바이스 + 같은 주소)
  const used = useMemo(() => {
    const byKey = new Map(devices.map(d => [d.key, d]))
    return new Set(machine.items.filter(i => i.device && i.address.trim()).map(i => {
      const d = byKey.get(i.device)
      return pick(i.device, d ? addressKey(d.channelType, i.address) : i.address.trim())
    }))
  }, [machine.items, devices])
  const isUsed = (d: DeviceSpecDevice, address: string) => used.has(pick(d.key, addressKey(d.channelType, address)))

  const device = devices.find(d => d.key === deviceKey)
  const q = query.trim().toLowerCase()
  const tags = (device?.tags ?? []).filter(t => t.address.trim())
  const visible = tags.filter(t => !q || [t.address, t.alias, t.description].some(v => v.toLowerCase().includes(q)))
  const selectable = device ? visible : []
  const allSelected = selectable.length > 0 && selectable.every(t => selected.includes(pick(device!.key, t.address.trim())))

  const toggle = (k: string) => setSelected(s => (s.includes(k) ? s.filter(x => x !== k) : [...s, k]))
  const toggleAll = () => {
    if (!device) return
    const keys = selectable.map(t => pick(device.key, t.address.trim()))
    setSelected(s => (allSelected ? s.filter(k => !keys.includes(k)) : [...s, ...keys.filter(k => !s.includes(k))]))
  }
  const countOf = (d: DeviceSpecDevice) => selected.filter(k => k.startsWith(`${d.key}\u0000`)).length

  const add = () => {
    const byKey = new Map(devices.map(d => [d.key, d]))
    onAdd(selected.map(k => {
      const [dk, address] = k.split('\u0000')
      const tag = byKey.get(dk)?.tags.find(t => t.address.trim() === address)
      return { key: newKey(), code: codeOf(machine.code, tag?.alias ?? '', prefix), description: tag?.description.trim() ?? '', device: dk, address }
    }))
  }

  // Esc 는 이 팝업만 닫는다 (아래 편집 화면으로 넘기지 않는다)
  const closeRef = useRef(onClose)
  useEffect(() => { closeRef.current = onClose })
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); closeRef.current() } }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [])

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[60] p-4" onMouseDown={onClose}>
      <div className="bg-surface rounded-xl shadow-xl w-full max-w-5xl h-[80vh] flex flex-col overflow-hidden" onMouseDown={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100 shrink-0">
          <h3 className="font-semibold text-gray-800">태그 불러오기</h3>
          <span className="text-xs text-gray-400">
            <span className="font-mono text-gray-600">{machine.code || '(ID 없음)'}</span>에 추가합니다
          </span>
          <button onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-600" aria-label="닫기">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="flex-1 min-h-0 flex">
          <div className="w-60 shrink-0 border-r border-gray-100 overflow-y-auto py-2">
            {withTags.length === 0 && <p className="px-4 py-3 text-xs text-gray-400">태그가 등록된 디바이스가 없습니다. 디바이스 탭에서 태그를 먼저 입력하세요.</p>}
            {withTags.map(d => {
              const count = countOf(d)
              return (
                <button key={d.key} onClick={() => setDeviceKey(d.key)}
                  className={`w-full text-left px-4 py-2 border-l-2 transition ${d.key === deviceKey ? 'border-blue-600 bg-blue-50' : 'border-transparent hover:bg-gray-50'}`}>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm truncate ${d.key === deviceKey ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>{deviceLabel(d)}</span>
                    {count > 0 && <span className="ml-auto shrink-0 px-1.5 rounded-full bg-blue-600 text-white text-[11px] font-medium">{count}</span>}
                  </div>
                  <div className="text-[11px] text-gray-400 font-mono truncate">{d.code || '코드 없음'} · 태그 {d.tags.filter(t => t.address.trim()).length}</div>
                </button>
              )
            })}
          </div>

          <div className="flex-1 min-w-0 flex flex-col">
            <div className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-100 shrink-0">
              <span className="text-xs text-gray-500">{device ? `${deviceLabel(device)} · 태그 ${tags.length}` : ''}</span>
              <input value={query} onChange={e => setQuery(e.target.value)} placeholder="주소, Alias, 설명 검색" autoFocus
                className="ml-auto border border-gray-300 rounded-lg px-3 py-1.5 text-xs w-56" />
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs sticky top-0 z-10">
                  <tr>
                    <th className="w-10 px-3 py-2 text-center">
                      <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={selectable.length === 0}
                        title={q ? '검색 결과 전체 선택' : '이 디바이스 전체 선택'} className="w-4 h-4 accent-blue-600 align-middle" />
                    </th>
                    <th className="text-left px-3 py-2">주소</th>
                    <th className="text-left px-3 py-2">Alias</th>
                    <th className="text-left px-3 py-2">설명</th>
                    <th className="text-left px-3 py-2">타입</th>
                    <th className="text-left px-3 py-2">전송코드 (넣을 값)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {visible.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400 text-sm">태그가 없습니다.</td></tr>
                  ) : visible.map(t => {
                    const k = pick(device!.key, t.address.trim())
                    const done = isUsed(device!, t.address)
                    const checked = selected.includes(k)
                    const code = codeOf(machine.code, t.alias, prefix)
                    return (
                      <tr key={t.key} onClick={() => toggle(k)}
                        className={`cursor-pointer ${checked ? 'bg-blue-50' : 'hover:bg-gray-50'} ${done && !checked ? 'text-gray-400' : ''}`}>
                        <td className="px-3 py-2 text-center">
                          <input type="checkbox" checked={checked} readOnly className="w-4 h-4 accent-blue-600 pointer-events-none" />
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-800">{t.address}</td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-600">{t.alias || '-'}</td>
                        <td className="px-3 py-2 text-xs text-gray-600">{t.description || '-'}</td>
                        <td className="px-3 py-2 text-xs text-gray-500">{t.dataType}</td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-500">
                          {code || <span className="font-sans text-gray-400">비움</span>}
                          {done && <span className="ml-1.5 font-sans text-[11px] text-amber-600">이미 있음</span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-100 px-5 py-3 shrink-0 flex items-center gap-3">
          <span className="text-xs text-gray-500">선택한 태그 {selected.length}개</span>
          {selected.length > 0 && <button onClick={() => setSelected([])} className="text-xs text-gray-400 hover:text-gray-600">선택 해제</button>}
          <div className="ml-auto flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs text-gray-600 mr-2 cursor-pointer select-none"
              title={prefix ? '전송코드 = 인터페이스 ID_Alias' : '전송코드 = Alias (인터페이스 ID를 붙이지 않음)'}>
              <input type="checkbox" checked={prefix} onChange={e => setPrefix(e.target.checked)} className="w-4 h-4 accent-blue-600" />
              전송코드에 <span className="font-mono text-gray-800">{machine.code.trim() || 'ID'}_</span> 붙이기
            </label>
            <button onClick={onClose} className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">취소</button>
            <button onClick={add} disabled={selected.length === 0}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-accent-hover disabled:opacity-50">
              {selected.length > 0 ? `${selected.length}개 추가` : '추가'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
